import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { css, cx } from '@emotion/css'
import {
  formSectionTitleCss,
  readOnlyGridCss
} from '../../legacy-design-system/text'
import { formatStyles } from '../react-intl-helpers'
import { Box, Row } from '../../legacy-design-system/form'
import {
  Checkbox,
  MultiSelect,
  NumberInput
} from '@contrast-security-inc/design-system-inputs'
import { Text } from '@contrast-security-inc/design-system-typography'
import {
  allImportances,
  appImportanceI18n,
  ApplicationImportance
} from '../application-importance'
import { useGetApplicationsBaseInfo } from './api/use-get-applications-base-info'
import { ApplicationBaseInfo } from './index'

export interface LowRouteCoveragePolicyFormProps {
  readonly organizationUuid: string
  readonly initialValues: {
    routeCoveragePercentage: number
    isRouteCoverageEnabled: boolean
    applicationIds: string[]
    applicationImportances: ApplicationImportance[]
  }
  readonly onChange: (values: {
    isRouteCoverageEnabled: boolean
    routeCoveragePercentage: number
    selectedApplicationIds: string[]
    selectedApplicationImportances: ApplicationImportance[]
  }) => void
}

export interface ApplicationOrImportanceOption {
  readonly id: string | ApplicationImportance
  readonly name: string
}

const normalizeApplications = (
  applications: ApplicationBaseInfo[]
): ApplicationOrImportanceOption[] => {
  return applications.map(({ appId, name }) => ({
    id: appId,
    name
  }))
}

const DEFAULT_ROUTE_COVERAGE_PERCENTAGE = 70
const ROUTE_COVERAGE_PERCENTAGE_MIN = 1
const ROUTE_COVERAGE_PERCENTAGE_MAX = 100

export function LowRouteCoveragePolicyForm({
  organizationUuid,
  initialValues,
  onChange
}: LowRouteCoveragePolicyFormProps) {
  const { formatMessage } = useIntl()

  const normalizeImportances = (
    importances: ApplicationImportance[]
  ): ApplicationOrImportanceOption[] => {
    return importances.map((importance) => ({
      id: importance,
      name: formatMessage({ id: appImportanceI18n[importance] })
    }))
  }
  const { applications } = useGetApplicationsBaseInfo({ organizationUuid })

  const [isRouteCoverageEnabled, setIsRouteCoverageEnabled] = React.useState(
    initialValues.isRouteCoverageEnabled ?? false
  )
  const [routeCoveragePercentage, setRouteCoveragePercentage] = React.useState(
    initialValues.routeCoveragePercentage ?? DEFAULT_ROUTE_COVERAGE_PERCENTAGE
  )
  const [
    selectedApplicationsOrImportances,
    setSelectedApplicationsOrImportances
  ] = React.useState<ApplicationOrImportanceOption[]>(
    normalizeImportances(initialValues.applicationImportances ?? [])
  )

  React.useEffect(() => {
    onChange({
      isRouteCoverageEnabled,
      routeCoveragePercentage,
      selectedApplicationIds: selectedApplicationsOrImportances
        .filter(
          ({ id }) => !allImportances.includes(id as ApplicationImportance)
        )
        .map(({ id }) => id),
      selectedApplicationImportances: selectedApplicationsOrImportances
        .filter(({ id }) =>
          allImportances.includes(id as ApplicationImportance)
        )
        .map(({ id }) => id as ApplicationImportance)
    })
  }, [
    isRouteCoverageEnabled,
    onChange,
    routeCoveragePercentage,
    selectedApplicationsOrImportances
  ])

  React.useEffect(() => {
    if (applications) {
      setSelectedApplicationsOrImportances([])
      setSelectedApplicationsOrImportances([
        ...normalizeApplications(
          applications.filter(({ appId }) =>
            initialValues.applicationIds.includes(appId)
          )
        )
      ])
    }
  }, [applications, initialValues.applicationIds])

  return (
    <div>
      <FormattedMessage id="title_route_coverageAbsolutePolicy">
        {(msg) => (
          <span
            className={cx(
              formSectionTitleCss,
              css`
                margin-bottom: 10px;
              `
            )}
          >
            {msg}
          </span>
        )}
      </FormattedMessage>
      <div
        className={cx(
          readOnlyGridCss,
          css`
            margin-top: 8px;
            margin-bottom: 8px;
          `
        )}
      >
        {formatMessage(
          {
            id: 'detail_applicationSettings_routeCoverageAbsolutePolicy'
          },
          {
            ...formatStyles.doclink({
              path: 'configure-route-expiration.html'
            })
          }
        )}
      </div>
      <FormattedMessage id="detail_applicationSettings_routeCoverageAbsolutePolicyDescription" />
      <Row>
        <Box basis={12}>
          <Checkbox
            testId="low-route-coverage-policy-checkbox"
            label={formatMessage({
              id: 'detail_routeCoverageAbsolutePolicy_checkbox'
            })}
            isChecked={isRouteCoverageEnabled}
            onChange={(_, value) => {
              if (value !== undefined && value) {
                setIsRouteCoverageEnabled(value)
              } else {
                setIsRouteCoverageEnabled(false)
              }
            }}
          />
        </Box>
      </Row>
      {isRouteCoverageEnabled && (
        <>
          <Row>
            <Box basis={12}>
              <div
                className={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                `}
              >
                <div
                  className={css`
                    input {
                      width: 80px;
                    }
                  `}
                >
                  <NumberInput
                    testId="low-route-coverage-policy-percentage-input"
                    label={formatMessage({
                      id: 'detail_routeCoverageAbsolutePolicy_percentage'
                    })}
                    min={`${ROUTE_COVERAGE_PERCENTAGE_MIN}`}
                    max={`${ROUTE_COVERAGE_PERCENTAGE_MAX}`}
                    step="1"
                    value={routeCoveragePercentage.toString()}
                    onChange={(_, value) => {
                      if (Number(value)) {
                        if (Number(value) > ROUTE_COVERAGE_PERCENTAGE_MAX) {
                          setRouteCoveragePercentage(
                            ROUTE_COVERAGE_PERCENTAGE_MAX
                          )
                          return
                        }
                        setRouteCoveragePercentage(Number(value))
                      }
                    }}
                  />
                </div>
                <div
                  className={css`
                    padding-left: 5px;
                  `}
                >
                  <Text font="body" />
                  {formatMessage(
                    {
                      id: 'detail_routeCoverageAbsolutePolicy_percentagePart2'
                    },
                    {
                      ...formatStyles.bold
                    }
                  )}
                </div>
              </div>
            </Box>
          </Row>
          <Row>
            <Box basis={6}>
              <FormattedMessage
                id={'detail_routeCoverageAbsolutePolicy_appSelectInfo'}
              >
                {(msg) => <span className={cx(readOnlyGridCss)}>{msg}</span>}
              </FormattedMessage>
              <MultiSelect<ApplicationOrImportanceOption>
                testId="low-route-coverage-policy-select"
                label={formatMessage({
                  id: 'detail_routeCoverageAbsolutePolicy_searchApplications'
                })}
                placeholderTag={`${formatMessage(
                  {
                    id: 'variable_allApplicationsLicensed'
                  },
                  { count: applications?.length ?? 0 }
                )}`}
                value={selectedApplicationsOrImportances}
                items={[
                  ...normalizeApplications(applications ?? []),
                  ...normalizeImportances(allImportances)
                ]}
                itemIsAnchored={({ id }) =>
                  allImportances.includes(id as ApplicationImportance)
                }
                itemToID={(item) => item?.id ?? ''}
                itemToString={(item) => item?.name ?? ''}
                onChange={(_, value) => {
                  // @ts-ignore
                  setSelectedApplicationsOrImportances(value)
                }}
              />
            </Box>
          </Row>
        </>
      )}
    </div>
  )
}
